import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { TYPOGRAPHY_STYLE } from 'radiance-ui';
import { MEDIA_QUERIES, SPACER } from 'radiance-ui/lib/constants';

import { LOCAL_COLORS } from 'styles/constants';
import { desktopOnly, mobileOnly } from 'styles/mixins/displayMobileDesktop';

export const OuterContainer = styled.div<{ themeType: string }>`
  display: flex;
  justify-content: center;
  align-items: center;

  background-color: ${({ themeType }) => {
    switch (themeType) {
      case 'primary':
        return LOCAL_COLORS.surfaceLight;
      case 'secondary':
        return LOCAL_COLORS.surfaceBrandBold;
      case 'tertiary':
        return LOCAL_COLORS.surfaceDarkBold;
      default:
        return LOCAL_COLORS.surfaceLight;
    }
  }};

  h2,
  small,
  p {
    color: ${({ themeType }) => {
      switch (themeType) {
        case 'primary':
          return LOCAL_COLORS.surfaceDarkBold;
        case 'secondary':
          return LOCAL_COLORS.surfaceLight;
        case 'tertiary':
          return LOCAL_COLORS.surfaceLight;
        default:
          return LOCAL_COLORS.surfaceDarkBold;
      }
    }};
  }
`;
export const InnerContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  padding: ${SPACER.medium};

  ${MEDIA_QUERIES.mdUp} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 7rem ${SPACER.x5large};
  }
`;

export const BeforeAfterContainer = styled.div`
  flex: 0.5;
`;

export const BeforeAfterContentContainer = styled.div`
  flex: 1;
`;

export const BeforeAfterImages = styled.div`
  display: flex;
`;

export const disclaimerStyles = css`
  margin-bottom: ${SPACER.base};
  display: block;
`;

export const BeforeAfterImageContainer = styled.div`
  :first-of-type {
    margin-right: ${SPACER.medium};
  }

  ${MEDIA_QUERIES.mdUp} {
    :first-of-type {
      margin-right: ${SPACER.xlarge};
    }
  }
`;

export const BeforeAfterImageMobile = styled.div`
  margin-bottom: ${SPACER.large};
  ${mobileOnly()}
`;

export const BeforeAfterImageDesktop = styled.div`
  margin-bottom: ${SPACER.large};
  ${desktopOnly()}
`;

export const BeforeAfterLabel = styled.div`
  ${({ theme }) => TYPOGRAPHY_STYLE.button(theme)};
  color: ${({ theme }) => theme.COLORS.primary};
  text-align: center;
`;

export const QuoteContainer = styled.div`
  margin-bottom: ${SPACER.x2large};
  flex: 1;

  ${MEDIA_QUERIES.mdUp} {
    margin-bottom: 0;
    margin-left: ${SPACER.x3large};
    max-width: 496px;
  }
`;

export const Quotation = styled.div`
  font-size: 75px;
  line-height: 60px;
  font-weight: 500;
  color: ${({ theme }) => theme.COLORS.primary};
  margin-bottom: -${SPACER.medium};

  ${MEDIA_QUERIES.mdUp} {
    font-size: 88px;
  }
`;

export const quoteStyles = css`
  margin-bottom: ${SPACER.xlarge};
`;

export const QuoteCredit = styled.div`
  ${({ theme }) => TYPOGRAPHY_STYLE.button(theme)};
  color: ${({ theme }) => theme.COLORS.primary};
`;

export const BeforeAfterPopContainer = styled.div<{ themeType: string }>`
  background-color: ${({ themeType }) =>
    themeType === 'primary'
      ? LOCAL_COLORS.surfaceWhite
      : LOCAL_COLORS.surfacePastelPurple};
`;

export const InnerBox = styled.div`
  padding: 80px 0;

  ${MEDIA_QUERIES.mdUp} {
    max-width: 1296px;
    padding: 80px ${SPACER.xlarge};
  }

  ${MEDIA_QUERIES.xlUp} {
    padding: 100px 72px;
  }

  ${MEDIA_QUERIES.xxlUp} {
    padding: 120px 0;
    margin: auto;
  }
`;

export const Header = styled.h2`
  font-size: ${SPACER.xlarge};
  font-weight: 700;
  line-height: 110%;
  letter-spacing: -0.64px;

  padding-bottom: ${SPACER.large};

  ${MEDIA_QUERIES.mdUp} {
    font-size: ${SPACER.xlarge};
    margin: 0;
    max-width: 200px;
  }

  ${MEDIA_QUERIES.xlUp} {
    font-size: 52px;
    max-width: 600px;
  }
`;

export const HeaderContent = styled.div`
  ${MEDIA_QUERIES.mdUp} {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;

    > div {
      width: fit-content;
    }
  }
`;

export const SubheaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${MEDIA_QUERIES.mdUp} {
    > div {
      width: fit-content;
    }

    button {
      font-size: 16px;
      font-weight: 700;
      line-height: 132%;
      letter-spacing: -0.32px;
    }
  }
`;

export const Subheader = styled.p`
  font-size: 16px;
  line-height: 132%;
  padding-bottom: ${SPACER.xlarge};

  ${MEDIA_QUERIES.mdUp} {
    font-size: 16px;
    max-width: 400px;
  }

  ${MEDIA_QUERIES.xlUp} {
    font-size: 24px;
    line-height: 140%;
    max-width: 600px;
  }
`;

export const ExamplesContainer = styled.div`
  display: flex;
  gap: ${SPACER.medium};
  overflow: auto;
  padding: 0 ${SPACER.medium} ${SPACER.medium};
  margin-bottom: ${SPACER.xlarge};

  ${MEDIA_QUERIES.mdUp} {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    min-width: unset;
    padding: 0;
    margin: ${SPACER.x5large} 0 0;
  }

  ${MEDIA_QUERIES.xlUp} {
    gap: ${SPACER.large};
  }
`;

export const Disclaimer = styled.small`
  font-size: 14px;
  line-height: 140%;
  margin: ${SPACER.medium};

  ${MEDIA_QUERIES.mdUp} {
    margin: ${SPACER.medium} 0;
  }
`;

export const DesktopCtaContainer = styled.div`
  display: none;
  flex-direction: column;
  gap: ${SPACER.medium};

  ${MEDIA_QUERIES['mdUp']} {
    display: flex;
  }
`;
